import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import CTABanner from '../../components/CTABanner';
import { usePageMeta, useStructuredData } from '../../lib/seo';
import { SITE_URL } from '../../config/productFacts';
import CITIES from '../../data/tankstellenCities.json';
import { FUELS, FuelTabs, StationList, DataAttribution, fmtPrice } from '../tankstellen/tankstellenShared';
import { ClusterHero, FaqSection, CrossLinkChips, FuelXBox, breadcrumbSchema, faqSchema, getFuelPref, setFuelPref } from './clusterShared';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SpritpreiseCityPage() {
  const { slug } = useParams();
  const city = useMemo(() => CITIES.find((c) => c.slug === slug), [slug]);
  const name = city?.name || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fuel, setFuel] = useState(() => getFuelPref() || 'e5');

  usePageMeta(
    `Spritpreise ${name} aktuell: Benzin & Diesel heute | FuelX`,
    `Aktuelle Spritpreise in ${name} live vergleichen: Super E5, E10 & Diesel mit günstigster Tankstelle, DE-Vergleich & Spar-Rechnung — alle 15 Minuten aktualisiert.`,
    { canonical: `${SITE_URL}/spritpreise/${slug}` }
  );

  useEffect(() => {
    if (!slug || !city) return;
    let active = true;
    setLoading(true);
    setData(null);
    axios
      .get(`${API}/api/tankstellen/city/${slug}`)
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setData({ status: 'error', stations: [], stats: {} }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug, city]);

  const faq = useMemo(() => [
    [`Wie hoch sind die Spritpreise in ${name} heute?`, `Die aktuellen Preise für Super E5, E10 und Diesel in ${name} siehst du oben — live aus der amtlichen Markttransparenzstelle für Kraftstoffe (MTS-K), alle 15 Minuten aktualisiert.`],
    [`Wo ist Tanken in ${name} am billigsten?`, `Die günstigste Tankstelle je Kraftstoffsorte wird oben hervorgehoben. Der Preisunterschied innerhalb von ${name} beträgt oft mehr als 10 Cent pro Liter — der Vergleich direkt vor dem Tanken lohnt sich fast immer.`],
    [`Wann sind die Spritpreise in ${name} am niedrigsten?`, 'In der Regel abends zwischen 18 und 22 Uhr — im Schnitt 8 bis 14 Cent günstiger als morgens zwischen 6 und 9 Uhr.'],
  ], [name]);

  const schema = useMemo(() => {
    if (!city) return null;
    return {
      '@graph': [
        breadcrumbSchema([['FuelX', ''], ['Spritpreise', '/spritpreise'], [`Spritpreise ${city.name}`, `/spritpreise/${city.slug}`]]),
        faqSchema(faq),
      ],
    };
  }, [city, faq]);
  useStructuredData(schema, `spritpreise-${slug}`);

  if (!city) return <Navigate to="/spritpreise" replace />;

  const stats = data?.stats || {};
  const national = data?.national || {};
  const activeStat = stats[fuel];
  const fuelLabel = FUELS.find((f) => f.key === fuel)?.label || '';
  const spreadCt = activeStat ? Math.round((activeStat.max - activeStat.min) * 100) : null;
  const neighbors = data?.neighbors || [];

  const onFuelChange = (f) => { setFuel(f); setFuelPref(f); };

  return (
    <div className="min-h-screen bg-white pt-[92px]" data-testid="spritpreise-city-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-8 pt-12 sm:pt-16 pb-16">
        <ClusterHero
          crumbs={[['FuelX', '/'], ['Spritpreise', '/spritpreise'], [name, null]]}
          eyebrow={`${city.bundesland ? `${city.bundesland} · ` : ''}Live-Spritpreise`}
          h1={<>Spritpreise {name}:<br />Benzin &amp; Diesel aktuell</>}
          intro={
            data?.station_count
              ? `Live-Preise von ${data.station_count} Tankstellen in und um ${name} — Super E5, E10 und Diesel im direkten Vergleich mit dem Bundesdurchschnitt. Amtliche MTS-K-Daten, alle 15 Minuten aktualisiert.`
              : `Aktuelle Spritpreise für ${name} im Live-Vergleich — Super E5, E10 und Diesel mit günstigster Tankstelle und DE-Vergleich. Amtliche MTS-K-Daten, alle 15 Minuten aktualisiert.`
          }
          testId="sp-city"
        />

        {/* Preis-Statistik */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-10" data-testid="sp-city-stats">
          {FUELS.map((f) => {
            const st = stats[f.key];
            const nat = national[f.key];
            const deltaCt = st && typeof nat === 'number' ? Math.round((st.avg - nat) * 100) : null;
            return (
              <button key={f.key} onClick={() => onFuelChange(f.key)} className={`rounded-2xl border p-5 text-left transition-colors ${fuel === f.key ? 'border-stone-900 bg-stone-950 text-white' : 'border-stone-200 bg-white hover:border-stone-400'}`} data-testid={`sp-city-stat-${f.key}`}>
                <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${fuel === f.key ? 'text-emerald-400' : 'text-stone-400'}`}>{f.label} · ab</p>
                <p className={`mt-2 text-3xl font-medium tabular-nums tracking-tight ${fuel === f.key ? 'text-white' : 'text-stone-900'}`}>
                  {loading ? '···' : fmtPrice(st?.min)}
                </p>
                <p className={`mt-1.5 text-[12px] ${fuel === f.key ? 'text-stone-400' : 'text-stone-500'}`}>
                  Ø {fmtPrice(st?.avg)}
                  {deltaCt !== null && (
                    <span className={`ml-2 font-semibold ${deltaCt <= 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                      {deltaCt <= 0 ? `${Math.abs(deltaCt)} ct unter` : `${deltaCt} ct über`} DE-Ø
                    </span>
                  )}
                </p>
              </button>
            );
          })}
        </div>

        {activeStat?.cheapest_station && (
          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4" data-testid="sp-city-cheapest">
            <p className="text-[14px] text-stone-700">
              <strong className="text-stone-900">Günstigster {fuelLabel} in {name} jetzt:</strong>{' '}
              {activeStat.cheapest_station.brand || activeStat.cheapest_station.name}
              {activeStat.cheapest_station.street ? `, ${activeStat.cheapest_station.street}` : ''} — <strong className="text-emerald-700 tabular-nums">{fmtPrice(activeStat.min)}</strong>
            </p>
          </div>
        )}

        {/* Top-10 Stationen */}
        <div className="mt-12">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <h2 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] text-stone-900">
              Die 10 günstigsten Preise in {name}
            </h2>
            <FuelTabs fuel={fuel} onChange={onFuelChange} />
          </div>
          {loading ? (
            <div className="rounded-2xl border border-stone-200 divide-y divide-stone-100 overflow-hidden animate-pulse" data-testid="sp-city-loading">
              {[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-stone-50" />)}
            </div>
          ) : (
            <StationList stations={data?.stations || []} fuel={fuel} limit={10} testIdPrefix="sp-city-station" />
          )}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <DataAttribution fetchedAt={data?.fetched_at} />
            <Link to={`/tankstellen/${slug}`} className="text-[13px] font-semibold text-emerald-700 hover:text-emerald-900 transition-colors" data-testid="sp-city-all-stations-link">
              Alle Tankstellen in {name} mit Karte →
            </Link>
          </div>
        </div>

        {/* Spar-Rechnung */}
        <section className="mt-14 text-[15.5px] text-stone-600 leading-[1.75]" data-testid="sp-city-savings">
          <h2 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] text-stone-900 mb-4">
            Was der Preisvergleich in {name} konkret bringt
          </h2>
          {spreadCt !== null && spreadCt > 0 ? (
            <p className="mb-4">
              Zwischen der günstigsten und teuersten Station in {name} liegen aktuell{' '}
              <strong className="text-stone-900">{spreadCt} Cent pro Liter {fuelLabel}</strong> — bei einer 50-Liter-Tankfüllung sind das{' '}
              <strong className="text-emerald-700">{(spreadCt * 0.5).toFixed(2).replace('.', ',')} €</strong> Unterschied. Wer zusätzlich abends statt morgens tankt, spart weitere 8 bis 14 Cent pro Liter.
            </p>
          ) : (
            <p className="mb-4">
              Innerhalb von {name} liegen günstigste und teuerste Tankstelle typischerweise über 10 Cent pro Liter auseinander — bei einer 50-Liter-Füllung mehr als 5 € pro Tankvorgang. Abends tanken (18–22 Uhr) spart zusätzlich.
            </p>
          )}
          <p>
            Wie viel dich deine Strecken wirklich kosten, zeigt dir der <Link to="/spritkostenrechner" className="font-semibold text-emerald-700 hover:text-emerald-900">Spritkostenrechner</Link> — mit den Live-Preisen von heute.
          </p>
        </section>

        <FuelXBox title={`Noch mehr sparen in ${name}`} testId="sp-city-fuelx-box">
          <p>
            Mit der FuelX Card sparst du bei aktiver Nutzung qualifizierender Angebote effektiv <strong>Ø 0,40 € pro Liter</strong> — zusätzlich zum Preisvergleich. Dazu kommen 37 Auto-Leistungen wie TÜV-, Kfz-Steuer- und Werkstatt-Vorteile.
          </p>
        </FuelXBox>

        <FaqSection title={`Häufige Fragen zu Spritpreisen in ${name}`} items={faq} testId="sp-city-faq" />

        {neighbors.length > 0 && (
          <CrossLinkChips
            title="Spritpreise in der Umgebung"
            links={neighbors.map((n) => [`Spritpreise ${n.name}`, `/spritpreise/${n.slug}`])}
            testId="sp-city-neighbors"
          />
        )}

        <CrossLinkChips
          links={[
            [`Alle Tankstellen in ${name} + Karte`, `/tankstellen/${slug}`],
            [`Dieselpreise ${name}`, `/dieselpreise/${slug}`],
            ['Günstig tanken: Alle Tipps', '/guenstig-tanken'],
            ['Spritkostenrechner', '/spritkostenrechner'],
            ['Spritpreise Deutschland', '/spritpreise'],
          ]}
          testId="sp-city-links"
        />
      </div>

      <CTABanner
        variant="light"
        headline={<>Tanken in {name} ist nur der Anfang.<br /><span className="text-stone-400">Alle Autokosten. Eine Card.</span></>}
        text="Mit der FuelX Card sparst du effektiv Ø 0,40 € pro Liter und regelst Versicherung, TÜV, Werkstatt & Termine an einem Ort."
        buttonText="FuelX Card entdecken"
        buttonTo="/fuelx-card"
      />
    </div>
  );
}
